import { Gesture } from "@-/com.npmjs.react-native-gesture-handler";
import {
  type SharedValue,
  useFrameCallback,
  useReducedMotion,
  useSharedValue,
  withTiming,
  withDelay,
} from "@-/com.npmjs.react-native-reanimated";
import { useVisualActivity } from "@-/dev.improve.haniverse.lib.visual-activity";
import { useEffect, useMemo } from "react";
import {
  WELCOME_GALAXY_SPIN_DEAD_RADIUS,
  welcomeGalaxyDiskPointAt,
  welcomeGalaxyFlingSpeed,
  wrapWelcomeGalaxyAngle,
} from "#welcome-galaxy-spin";
import type { WelcomeGalaxyViewport } from "#welcome-galaxy-timeline";

/** How quickly a fling's extra spin bleeds away (1/s); the slow idle spin carries on beneath it. */
const FLING_DECAY = 1.4;
/** Below this (rad/s) a fling has died out. */
const FLING_REST_SPEED = 0.002;
/** Longest frame step, so a hitch (or a return from the background) never lurches the spin. */
const MAX_FRAME_SECONDS = 0.05;

export type WelcomeGalaxySpinGesture = {
  gesture: ReturnType<typeof Gesture.Race>;
  /** 1 while the galaxy is held, easing back to 0 a moment after release. */
  interactionProgress: SharedValue<number>;
  /** The user's accumulated spin (rad), added on top of the timeline's slow idle spin. */
  spinOffset: SharedValue<number>;
};

/**
 * Touching the welcome galaxy: a tap fast-forwards the intro, and a drag turns the disk like a
 * turntable, flinging it on release. The fling's extra speed decays away, leaving the timeline's
 * slow perpetual spin, which never stops. Any touch also fast-forwards the intro, since the user
 * evidently wants the galaxy in hand. Under reduced motion a drag still turns the disk, but a
 * release doesn't fling it.
 *
 * The drag only accumulates while touch events arrive; `spinOffset` takes it up once per frame,
 * alongside the clock, so the scene is computed and drawn once per frame however fast the
 * touches come.
 */
export function useWelcomeGalaxySpinGesture({
  onTouch,
  viewport,
}: {
  /** Called on the UI thread, so it must be a worklet. */
  onTouch: () => void;
  viewport: WelcomeGalaxyViewport;
}): WelcomeGalaxySpinGesture {
  const reduceMotion = useReducedMotion();
  const isVisuallyActive = useVisualActivity();
  const spinOffset = useSharedValue(0);
  const interactionProgress = useSharedValue(0);
  const draggedSpin = useSharedValue(0);
  const flingSpeed = useSharedValue(0);
  const lastAngle = useSharedValue(0);
  const { height, stageWidth, width } = viewport;

  const frameCallback = useFrameCallback((frame) => {
    "worklet";
    const dragged = draggedSpin.get();
    const speed = flingSpeed.get();
    const isFlinging = Math.abs(speed) >= FLING_REST_SPEED;
    if (dragged === 0 && !isFlinging) {
      return;
    }
    let spin = spinOffset.get() + dragged;
    if (isFlinging) {
      const seconds = Math.min((frame.timeSincePreviousFrame ?? 16) / 1000, MAX_FRAME_SECONDS);
      spin += speed * seconds;
      flingSpeed.set(speed * Math.exp(-FLING_DECAY * seconds));
    }
    draggedSpin.set(0);
    spinOffset.set(spin);
  }, false);

  const { setActive } = frameCallback;
  useEffect(() => {
    setActive(isVisuallyActive);
    return () => {
      setActive(false);
    };
  }, [isVisuallyActive, setActive]);

  const gesture = useMemo(() => {
    const size = { height, stageWidth, width };
    const tap = Gesture.Tap().onEnd((_event, success) => {
      "worklet";
      if (success) {
        onTouch();
      }
    });
    const pan = Gesture.Pan()
      .maxPointers(1)
      .onBegin(() => {
        "worklet";
        interactionProgress.set(withTiming(1, { duration: reduceMotion ? 0 : 220 }));
      })
      .onFinalize(() => {
        "worklet";
        interactionProgress.set(
          withDelay(reduceMotion ? 0 : 700, withTiming(0, { duration: reduceMotion ? 0 : 600 })),
        );
      })
      .onStart((event) => {
        "worklet";
        onTouch();
        flingSpeed.set(0);
        lastAngle.set(welcomeGalaxyDiskPointAt(event.x, event.y, size).angle);
      })
      .onChange((event) => {
        "worklet";
        const point = welcomeGalaxyDiskPointAt(event.x, event.y, size);
        if (point.radius >= WELCOME_GALAXY_SPIN_DEAD_RADIUS) {
          draggedSpin.set(
            draggedSpin.get() + wrapWelcomeGalaxyAngle(point.angle - lastAngle.get()),
          );
        }
        lastAngle.set(point.angle);
      })
      .onEnd((event) => {
        "worklet";
        if (!reduceMotion) {
          flingSpeed.set(
            welcomeGalaxyFlingSpeed(event.x, event.y, event.velocityX, event.velocityY, size),
          );
        }
      });
    return Gesture.Race(pan, tap);
  }, [
    draggedSpin,
    flingSpeed,
    height,
    interactionProgress,
    lastAngle,
    onTouch,
    reduceMotion,
    stageWidth,
    width,
  ]);

  return { gesture, interactionProgress, spinOffset };
}
